/**
 * operator_api_credentials (+ scopes, + usage) — Phase 18 / Task 18.1.
 *
 * The credential model behind the Operator Public API. Three tables:
 *
 *   1. `operator_api_credentials`       — the credential record itself.
 *   2. `operator_api_credential_scopes` — per-credential scope grants.
 *   3. `operator_api_credential_usage`  — per-credential monthly counters.
 *
 * Migration 0112 is authoritative for every constraint, index, policy and GRANT;
 * this file is the Drizzle view of it.
 *
 * ── Decisions this shape implements ────────────────────────────────────────
 *
 * DEC-238 (D-1) — the credential is an API KEY, not OAuth2. A key is what an
 * unattended SCADA/ETL client can actually hold. The shape is deliberately
 * additive-friendly: nothing here names "key" in a way that would have to change
 * if OAuth2 client-credentials is added alongside it later.
 *
 * DEC-239 (D-2) — the HAULER tenant issues; Paelode retains revocation as a
 * safety valve. So `tenant_id` is the ISSUING HAULER, which is also what makes
 * it the RLS scope and (per DEC-248) the routing key for a future
 * `POST /haul-requests`. `revoked_by_actor` records which side pulled the lever,
 * because "revoked" alone loses the fact that matters.
 *
 * DEC-240 (D-3) — the tier gate read `tenants.operator_tier` (since OPP PR 6 the gate is the
 * operator's OP_API plan feature; OPP PR 13a removed the tier guard). It was a column
 * on a TENANT, which is why `operator_tenant_id` points at `tenants` rather than
 * at `platform_operators`. Task 18.2 then resolves it to a canonical operator id
 * through the existing `resolveCanonicalOperatorId` (merge-chain CYCLE guard,
 * 412 `OPERATOR_PORTAL_NOT_LINKED`) rather than storing a second copy of that
 * resolution here.
 *
 * ── Secret handling ────────────────────────────────────────────────────────
 *
 * The raw secret is NEVER persisted. `secret_hash` is the SHA-256 hex of the
 * whole presented token, exactly mirroring `operator_magic_link_tokens`
 * (DEC-148): 32 bytes of `crypto.randomBytes` → base64url → SHA-256 hex at rest.
 * A database compromise leaks hashes, not usable credentials. There is no
 * "reveal" path anywhere in the API — the secret exists in one HTTP response, at
 * issuance, and then only in the holder's config.
 *
 * SHA-256 rather than bcrypt/argon2 is correct HERE and would not be for a
 * password: the input is 256 bits of CSPRNG output, so there is no dictionary to
 * run and no work factor worth paying on every API call.
 *
 * `key_id` is the NON-SECRET half: a short opaque handle that appears in the
 * token's visible prefix, in audit payloads, in 18.2's per-call log lines and in
 * 18.9's usage rows, so a credential is identifiable in all of those places
 * without any of them ever naming its secret. It is NOT the lookup key —
 * verification hashes the whole token and matches `secret_hash`, which keeps the
 * lookup a single indexed equality with no parsing and no partial-match timing
 * surface.
 *
 * ── RLS ────────────────────────────────────────────────────────────────────
 *
 * All three tables are tenant-scoped on the ISSUING HAULER with the 0050
 * convention: `ENABLE ROW LEVEL SECURITY` + a policy carrying both `USING` and
 * `WITH CHECK` + explicit GRANTs. The two child tables additionally carry a
 * COMPOSITE foreign key `(tenant_id, credential_id)` into
 * `(tenant_id, id)` — RLS alone would not stop a scope row from attaching itself
 * to another tenant's credential, because the policy only ever examines the
 * child row's own `tenant_id`. Same shape as `hauler_operators`'
 * import-batch ownership FK (migration 0107).
 *
 * Spec: `.planning/phases/18-operator-public-api/18-PLAN.md` Task 18.1;
 *       master spec §6 (three-layer security), §32 (audit), §67 (API versioning).
 */

import {
  bigint,
  char,
  check,
  date,
  index,
  pgPolicy,
  pgTable,
  primaryKey,
  text,
  timestamp,
  unique,
  uuid,
  varchar,
} from 'drizzle-orm/pg-core';
import { sql } from 'drizzle-orm';
import { appUserRole } from './enums.js';
import { tenants, users } from './users.js';

/**
 * OPERATOR_API_SCOPES — the v1 scope vocabulary.
 *
 * Lowercase `resource:action`, deliberately unlike the UPPER_SNAKE enums
 * elsewhere in this schema: these strings are part of a PUBLIC API contract —
 * they appear in issuance requests, in the OpenAPI document (18.10) and in a
 * caller's own configuration — and lowercase colon-separated scopes are the
 * universal convention there. An internal enum nobody outside the product ever
 * types is a different kind of string.
 *
 * The resource list is Task 18.0's catalog; the first four are the first release
 * (read-only per `18-CONTEXT.md` §2) plus the write scope 18.6 needs. Adding a
 * scope is a migration (the CHECK) plus a line here — intentionally not free,
 * because a scope is a promise to a caller. The two MUST be widened in lockstep:
 * a scope listed here with no CHECK value fails issuance at the constraint, and a
 * CHECK value with no line here is unreachable, because `OperatorApiScopeSchema`
 * is built from this array.
 *
 * `lookups:read` (DEC-306, migration 0122) covers all three lookup endpoints —
 * `/sites`, `/wells` and `/job-types` — because they are one capability: the only
 * reason to read any of them is to fill in a `POST /haul-requests` body. It is
 * neither implied by nor implies `haul-requests:write`; `callerHasScope` is a plain
 * membership test with no hierarchy and no wildcard.
 */
export const OPERATOR_API_SCOPES = [
  'tickets:read',
  'volumes:read',
  'haul-requests:read',
  'haul-requests:write',
  'lookups:read',
  // Operator tank registry PR 4 (migration 0248): GET /tanks, the operator company's tanks.
  'tanks:read',
  // 13.9 (D-08, migration 0260): POST /tank-readings. Company-wide (OPERATOR) keys only.
  'readings:write',
] as const;
export type OperatorApiScope = (typeof OPERATOR_API_SCOPES)[number];

/**
 * 13.9 (D-08, DEC-372, migration 0260) — the two kinds of key.
 *
 *   HAULER_PAIR  reads one hauler's data for the operator (DEC-239); lives in the hauler's tenant.
 *   OPERATOR     company-wide: lives in the operator's own tenant, tied to no hauler, and holds
 *                only {@link OPERATOR_KEY_SCOPES}.
 *
 * 0260's trigger (operator_api_credential_scopes_kind_guard) enforces the split in the database:
 * an OPERATOR key holds nothing else, and a HAULER_PAIR key never holds `readings:write`.
 */
export const OPERATOR_API_KEY_KINDS = ['HAULER_PAIR', 'OPERATOR'] as const;
export type OperatorApiKeyKind = (typeof OPERATOR_API_KEY_KINDS)[number];

/** The scopes a company-wide (OPERATOR) key may hold. */
export const OPERATOR_KEY_SCOPES = ['tanks:read', 'readings:write'] as const;
export type OperatorKeyScope = (typeof OPERATOR_KEY_SCOPES)[number];

/** The scopes a hauler-pair key may hold: every scope but `readings:write`. */
export const HAULER_KEY_SCOPES = [
  'tickets:read',
  'volumes:read',
  'haul-requests:read',
  'haul-requests:write',
  'lookups:read',
  'tanks:read',
] as const satisfies readonly OperatorApiScope[];
export type HaulerKeyScope = (typeof HAULER_KEY_SCOPES)[number];

/** The scopes a credential may be issued with today (read-only first release + 18.6's write). */
export const OPERATOR_API_CREDENTIAL_STATUSES = ['ACTIVE', 'REVOKED'] as const;
export type OperatorApiCredentialStatus = (typeof OPERATOR_API_CREDENTIAL_STATUSES)[number];

/**
 * Who revoked a credential. DEC-239 splits the authority: the hauler owns the
 * commercial relationship and does the day-to-day revoking; Paelode keeps a
 * safety valve. Reading "REVOKED" without this column cannot tell a routine
 * hauler cleanup from Paelode shutting an integration down.
 */
export const OPERATOR_API_REVOKED_BY_ACTORS = ['HAULER', 'OPERATOR', 'PLATFORM'] as const;
export type OperatorApiRevokedByActor = (typeof OPERATOR_API_REVOKED_BY_ACTORS)[number];

/**
 * Who issued a credential (operator portal rework PR 9, migration 0213). Every key before 0213
 * was issued by its hauler; from 0213 only Paelode and the operator admin issue.
 */
export const OPERATOR_API_ISSUED_BY_ACTORS = ['HAULER', 'OPERATOR', 'PLATFORM'] as const;
export type OperatorApiIssuedByActor = (typeof OPERATOR_API_ISSUED_BY_ACTORS)[number];

export const operatorApiCredentials = pgTable(
  'operator_api_credentials',
  {
    id: uuid('id')
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    /** The ISSUING HAULER tenant (DEC-239). RLS scope. */
    tenantId: uuid('tenant_id')
      .notNull()
      .references(() => tenants.id, { onDelete: 'cascade' }),
    /** The OPERATOR tenant this credential acts for. See the header on why a tenant. */
    operatorTenantId: uuid('operator_tenant_id')
      .notNull()
      .references(() => tenants.id, { onDelete: 'cascade' }),
    /**
     * Hauler-entered display name ("DEVON SCADA NIGHTLY"). DEC-147 makes this an
     * IDENTIFIER, so it is uppercased server-side on write. Deliberately NOT
     * unique: a rotation keeps the label so the hauler still recognises the
     * credential, which means two ACTIVE rows share it for the overlap window.
     */
    label: varchar('label', { length: 120 }).notNull(),
    /** SHA-256 hex of the presented token. The raw token is never persisted. */
    secretHash: char('secret_hash', { length: 64 }).notNull().unique(),
    /** Non-secret handle. Appears in the token prefix, audit payloads and logs. */
    keyId: varchar('key_id', { length: 32 }).notNull().unique(),
    status: text('status').notNull().default('ACTIVE'),
    /**
     * NULL = no expiry. A rotation sets this on the PREDECESSOR to the end of the
     * overlap window. Expiry is not revocation: it is the scheduled end of a
     * rotation, with no actor and no reason.
     */
    expiresAt: timestamp('expires_at', { withTimezone: true }),
    /** The successor a rotation created. NULL on a credential never rotated. */
    rotatedToId: uuid('rotated_to_id'),
    rotatedAt: timestamp('rotated_at', { withTimezone: true }),
    revokedAt: timestamp('revoked_at', { withTimezone: true }),
    revokedByUserId: uuid('revoked_by_user_id').references(() => users.id, {
      onDelete: 'set null',
    }),
    revokedByActor: text('revoked_by_actor'),
    /**
     * Free-text prose, NOT an identifier — the DEC-147 notes/comments exemption.
     * Preserved exactly as typed.
     */
    revokeReason: text('revoke_reason'),
    /** Stamped by 18.2 on each authenticated call. NULL = never used. */
    lastUsedAt: timestamp('last_used_at', { withTimezone: true }),
    createdByUserId: uuid('created_by_user_id').references(() => users.id, {
      onDelete: 'set null',
    }),
    /** HAULER | OPERATOR | PLATFORM (0213). Who issued it; created_by_user_id says which person. */
    issuedByActor: text('issued_by_actor').notNull().default('HAULER'),
    /** HAULER_PAIR | OPERATOR (0260, D-08). An OPERATOR key's tenant_id IS its operator_tenant_id. */
    keyKind: text('key_kind').notNull().default('HAULER_PAIR'),
    /**
     * The first key of this key's rotation chain (0260): itself on issue, the predecessor's root
     * on rotate. NULL on keys from before 0260 and on 0213's hauler-key rotations, so readers use
     * coalesce(root_credential_id, id). The composite FK (tenant_id, root_credential_id) is in the
     * migration only.
     */
    rootCredentialId: uuid('root_credential_id'),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp('updated_at', { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    /** The hauler's own list, and 18.2's "which credentials exist for this operator". */
    index('operator_api_credentials_tenant_operator_idx').on(t.tenantId, t.operatorTenantId),
    /**
     * Target of the child tables' composite FK. A plain UNIQUE on (tenant_id, id)
     * is redundant for uniqueness — `id` is already the PK — and exists solely so
     * `(tenant_id, credential_id)` has something to reference.
     */
    unique('operator_api_credentials_tenant_id_key').on(t.tenantId, t.id),
    check('operator_api_credentials_status_check', sql`status IN ('ACTIVE', 'REVOKED')`),
    check(
      'operator_api_credentials_revoked_by_actor_check',
      sql`revoked_by_actor IS NULL OR revoked_by_actor IN ('HAULER', 'OPERATOR', 'PLATFORM')`,
    ),
    check(
      'operator_api_credentials_issued_by_actor_check',
      sql`issued_by_actor IN ('HAULER', 'OPERATOR', 'PLATFORM')`,
    ),
    /**
     * `status` and `revoked_at` cannot disagree. The two ways they could are the
     * same bug in opposite directions: a REVOKED row with no timestamp loses when
     * it happened, and an ACTIVE row carrying a revoked_at reads as revoked in
     * every UI while still authenticating.
     */
    check(
      'operator_api_credentials_revocation_consistent_check',
      sql`(status = 'REVOKED') = (revoked_at IS NOT NULL)`,
    ),
    check(
      'operator_api_credentials_revocation_actor_check',
      sql`(revoked_at IS NULL) = (revoked_by_actor IS NULL)`,
    ),
    check(
      'operator_api_credentials_rotation_consistent_check',
      sql`(rotated_to_id IS NULL) = (rotated_at IS NULL)`,
    ),
    check('operator_api_credentials_rotation_self_check', sql`rotated_to_id IS DISTINCT FROM id`),
    check('operator_api_credentials_label_not_blank_check', sql`btrim(label) <> ''`),
    check('operator_api_credentials_key_kind_check', sql`key_kind IN ('HAULER_PAIR', 'OPERATOR')`),
    check(
      'operator_api_credentials_key_kind_tenant',
      sql`(key_kind = 'OPERATOR') = (tenant_id = operator_tenant_id)`,
    ),
    pgPolicy('operator_api_credentials_tenant_isolation', {
      as: 'permissive',
      to: appUserRole,
      for: 'all',
      using: sql`tenant_id::text = current_setting('app.current_tenant_id', true)`,
      withCheck: sql`tenant_id::text = current_setting('app.current_tenant_id', true)`,
    }),
  ],
);

export const operatorApiCredentialScopes = pgTable(
  'operator_api_credential_scopes',
  {
    id: uuid('id')
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    /** Denormalised from the parent so the RLS predicate needs no join. */
    tenantId: uuid('tenant_id')
      .notNull()
      .references(() => tenants.id, { onDelete: 'cascade' }),
    credentialId: uuid('credential_id').notNull(),
    scope: text('scope').notNull(),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    unique('operator_api_credential_scopes_credential_scope_key').on(t.credentialId, t.scope),
    check(
      'operator_api_credential_scopes_scope_check',
      sql`scope IN ('tickets:read', 'volumes:read', 'haul-requests:read',
          'haul-requests:write', 'lookups:read', 'tanks:read', 'readings:write')`,
    ),
    pgPolicy('operator_api_credential_scopes_tenant_isolation', {
      as: 'permissive',
      to: appUserRole,
      for: 'all',
      using: sql`tenant_id::text = current_setting('app.current_tenant_id', true)`,
      withCheck: sql`tenant_id::text = current_setting('app.current_tenant_id', true)`,
    }),
    // The composite FK (tenant_id, credential_id) -> (tenant_id, id) lives in the
    // migration only: Drizzle can express it, but the generated name and the
    // ON DELETE CASCADE are pinned there and 0112 is authoritative.
  ],
);

/**
 * Per-credential monthly counters.
 *
 * NOTHING WRITES THIS TABLE YET. It lands here because the 18.1 plan lists it
 * with the credential model and because migration numbers are a serialised,
 * cross-session resource — not because 18.1 has a counter to keep. Task 18.2
 * (per-call audit) and 18.3 (quotas) are the writers; 18.9 is the reader.
 *
 * Grain: one row per credential per UTC month, `period_start` holding the first
 * day of that month. Monthly because 18.3's quota concept is explicitly
 * "sustained monthly volume vs burst" and burst belongs in Redis with the
 * existing `publicProtection` limiter, not in Postgres. UTC because a quota
 * window is a contract boundary rather than something rendered to a human — the
 * "never display UTC" rule (DEC-004) binds the COUNT's presentation in 18.9, not
 * the bucket's arithmetic.
 *
 * Because no row has ever been written and no release reads it, 18.3 may still
 * reshape this freely — which is the one window in which that is cheap.
 */
export const operatorApiCredentialUsage = pgTable(
  'operator_api_credential_usage',
  {
    tenantId: uuid('tenant_id')
      .notNull()
      .references(() => tenants.id, { onDelete: 'cascade' }),
    credentialId: uuid('credential_id').notNull(),
    /** First day of the UTC month this row counts. */
    periodStart: date('period_start').notNull(),
    /** Total authenticated calls. bigint: a polling client can run millions a month. */
    requestCount: bigint('request_count', { mode: 'number' }).notNull().default(0),
    /** The write subset, counted separately because 18.3 limits writes separately. */
    writeCount: bigint('write_count', { mode: 'number' }).notNull().default(0),
    lastRequestAt: timestamp('last_request_at', { withTimezone: true }),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp('updated_at', { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    primaryKey({
      name: 'operator_api_credential_usage_pkey',
      columns: [t.credentialId, t.periodStart],
    }),
    index('operator_api_credential_usage_tenant_period_idx').on(t.tenantId, t.periodStart),
    check('operator_api_credential_usage_request_count_check', sql`request_count >= 0`),
    check('operator_api_credential_usage_write_count_check', sql`write_count >= 0`),
    check(
      'operator_api_credential_usage_write_le_request_check',
      sql`write_count <= request_count`,
    ),
    check(
      'operator_api_credential_usage_period_start_check',
      sql`period_start = date_trunc('month', period_start)::date`,
    ),
    pgPolicy('operator_api_credential_usage_tenant_isolation', {
      as: 'permissive',
      to: appUserRole,
      for: 'all',
      using: sql`tenant_id::text = current_setting('app.current_tenant_id', true)`,
      withCheck: sql`tenant_id::text = current_setting('app.current_tenant_id', true)`,
    }),
  ],
);

export type OperatorApiCredential = typeof operatorApiCredentials.$inferSelect;
export type NewOperatorApiCredential = typeof operatorApiCredentials.$inferInsert;
export type OperatorApiCredentialScope = typeof operatorApiCredentialScopes.$inferSelect;
export type NewOperatorApiCredentialScope = typeof operatorApiCredentialScopes.$inferInsert;
export type OperatorApiCredentialUsage = typeof operatorApiCredentialUsage.$inferSelect;
export type NewOperatorApiCredentialUsage = typeof operatorApiCredentialUsage.$inferInsert;
